import {
  undefinedOrTransformToDTOCurried,
  formatAddress,
  formatDate,
} from '@maia/utils';
import { IAnswerEntity } from '../../entitites/common/answer.entity';
import { IQuestionEntity } from '../../entitites/common/question.entity';
import { AddressDto } from '../common/address.dto';
import { PriceDto } from '../common/price.dto';
import { DocumentDto } from '../common/document.dto';
import { IDocumentEntity } from '../../entitites/common/document.entity';
import { IAddressEntity } from '../../shared';
import { match, P } from 'ts-pattern';

export class QuestionDto {
  readonly guid: string;
  readonly reference: string;
  readonly content: string;
  readonly title: string;
  readonly phrasing: string;
  readonly description: string;
  readonly icon: string;
  readonly inputType:
    | 'text'
    | 'number'
    | 'money'
    | 'radio'
    | 'date'
    | 'switch'
    | 'toggle_group'
    | 'question'
    | 'multiple_choice'
    | 'attachments'
    | 'dropdown'
    | null;
  readonly answer: AnswerDto;
  readonly options: Array<{
    value: string;
    label: string;
    isActive: boolean;
    isPositiveOption: boolean;
    isNegativeOption: boolean;
  }>;
  readonly position: string;
  readonly moreInformation: string;
  readonly children: Array<QuestionDto> | null;
  readonly price: PriceDto | null;
  readonly revalidateOnChange: boolean;
  readonly isRequired: boolean;
  readonly isEditable: boolean;

  constructor(entity: IQuestionEntity) {
    this.guid = entity.guid;
    this.reference = entity.reference;
    this.content = entity.content;
    this.title = entity.title;
    this.phrasing = entity.phrasing;
    this.description = entity.description;
    this.icon = entity.icon;
    this.inputType = entity.type;
    this.answer = new AnswerDto(entity.answer);
    this.options = entity.options.map((o) => ({
      value: o.value,
      label: o.label,
      isActive: o.is_active,
      isPositiveOption: o.is_positive_option,
      isNegativeOption: o.is_negative_option,
    }));
    this.position = entity.position;
    this.moreInformation = entity.more_information;
    this.children = entity.children?.map((c) => new QuestionDto(c)) ?? null;
    this.price = entity?.price ? new PriceDto(entity.price) : null;
    this.revalidateOnChange = entity.revalidate_on_change;
    this.isRequired = entity.is_required;
    this.isEditable = entity.is_editable;
  }
}

export class AnswerDto {
  inputType: number;
  value: string | number | Date | AddressDto | null | DocumentDto;

  constructor(entity: IAnswerEntity) {
    this.inputType = entity.input_type;
    this.value = this.getValue(entity);
  }

  getValue(entity: IAnswerEntity) {
    return (
      undefinedOrAddress(entity.address) ??
      undefinedOrDocument(entity.attachment) ??
      entity.text ??
      entity.number ??
      entity.date ??
      entity.multiple_choice
    );
  }

  toString(): string | null {
    return match(this.value)
      .with(
        P.when((v) => typeof v === 'string'),
        (value) => value,
      )
      .with(
        P.when((v) => typeof v === 'number'),
        (value) => value.toString(),
      )
      .with(
        P.when((v) => v instanceof Date),
        (value) => formatDate(value),
      )
      .with(
        P.when((v) => v instanceof AddressDto),
        (value) => formatAddress(value),
      )
      .with(
        P.when((v) => v instanceof DocumentDto),
        (value) => value.filename,
      )
      .otherwise(() => null);
  }
}

export const undefinedOrAddress = undefinedOrTransformToDTOCurried(
  (entity: IAddressEntity) => new AddressDto(entity),
);

export const undefinedOrDocument = undefinedOrTransformToDTOCurried(
  (entity: IDocumentEntity) => new DocumentDto(entity),
);

export class PutQuestionDto {
  questions: Array<{ guid: string; answer: string }>;

  constructor(params: Array<{ guid: string; answer: string }>) {
    this.questions = params.filter((question) => question.answer !== null);
  }
}

export class AnswerMutationPutDto {
  mutation_guid: string;
  customer_guid: string;
  questions: {
    questions: Array<{ guid: string; answer: string }>;
  };

  constructor(params: {
    mutationGuid: string;
    customerGuid: string;
    answers: Array<{ guid: string; answer: string }>;
  }) {
    this.mutation_guid = params.mutationGuid;
    this.customer_guid = params.customerGuid;
    this.questions = {
      questions: params.answers,
    };
  }
}
